import React, { useEffect, useState } from "react";
import { IconButton, makeStyles, Typography } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import { Add, Delete } from "@material-ui/icons";
import RemoveRoundedIcon from "@material-ui/icons/RemoveRounded";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import Tooltip from "@material-ui/core/Tooltip";

import AddDiseasesDialogComponent from "./components/addDiseasesDialogComponent/AddDiseasesDialogComponent";
import {
  changeDiseasesSelectedToRemoveAction,
  changeDiseasesToRemoveAction,
  changeDisplayedDiseasesAction,
  changeSelectedDiseasesAction,
} from "../../../../reducers/searchOmimState";
import { getPatientDiseasesService, patchIsDiagnoseService } from "../../../../services/commonservices";
import {
  storePatientDiagnosisActionCreator,
  storePatientDiseasesActionCreator,
} from "../../../../reducers/patientState";
import { DEFAULT_PAGE_SIZE } from "../../../../utils/constants";
import CustomToggleComponent from "../../../common/CustomToggleComponent";
import TagListComponent from "../../../common/TagListCompoents";
import familyBackgroundIcon from "../../../../images/gen/Family_background.svg";
import patientBackgroundIcon from "../../../../images/gen/Patient_background.svg";

const useStyles = makeStyles((theme) => ({
  dataContainer: {
    display: "grid",
    gap: "1rem",
    marginBottom: "2rem",
  },
  dataHeader: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  header: {
    display: "flex",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    color: theme.palette.secondary.main,
  },
  diagnosisContainer: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    alignItems: "flex-end",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  diagnosisTitle: {
    fontWeight: "bold",
  },
  dataGridContainer: {
    height: 400,
    width: "100%",
  },
  tableButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  actionsCell: {
    display: "flex",
    alignItems: "center",
  },
  formBlock: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    flex: "1",
  },
  diseaseOption: {
    textOverflow: "ellipsis",
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  primaryButton: {
    ...theme.primaryPinkButton,
    width: "auto",
    [theme.breakpoints.down("md")]: {},
  },
  secondaryButton: theme.secondaryPinkButton,
  textField: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 20,
    "& .Mui-focused": {
      borderRadius: 20,
      borderColor: theme.palette.secondary.main,
    },
    "&:focus-visible": {
      borderColor: "#2820D1 !important",
    },
    "& label.Mui-focused": {
      borderRadius: 20,
      color: theme.palette.secondary.main,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderRadius: 20,
        borderColor: theme.palette.secondary.main,
      },
    },
  },
  firstCap: {
    textTransform: "lowercase !important",
    "&:first-letter": {
      textTransform: "capitalize !important",
    },
  },
  iconButton: theme.iconButton,
  iconButtonDisable: theme.iconButtonDisable,
  bubble: {
    width: "1.5rem",
    height: "1.5rem",
    borderRadius: "50%",
    color: " #fff",
    backgroundColor: "#FF3185",
    placeItems: "center",
    display: "grid",
    position: "absolute",
    right: "-8px",
    top: "-10px",
  },
  iconContainer: {
    display: "flex",
    gap: theme.spacing(1),
  },
  svgIcon: {
    width: 28,
    height: 28,
  },
}));

const PatientDiagnosisComponent = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  const newPatient = !!JSON.parse(localStorage.getItem("genome-new-patient"));

  const loading = useSelector((state) => state.common.loading);

  const { patientData, diseases } = useSelector((state) => state.patient);

  const { selectedDiseases, diseasesToRemove, displayedDiseases, diseasesSelectedToRemove } = useSelector(
    (state) => state.searchOmim,
  );
  const mobileVersion = useSelector((state) => state.common.mobileVersion);

  const [diagnosis, setDiagnosis] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  const isDiseaseSelected = (id) => {
    return !!diseasesToRemove.find((disease) => disease === id);
  };

  const handleSelectDisease = (disease) => {
    let array = [...diseasesToRemove];

    const isSelected = isDiseaseSelected(disease);

    if (isSelected) {
      array = array.filter((s) => s !== disease);
    } else {
      array.push(disease);
    }

    dispatch(changeDiseasesToRemoveAction(array));
  };

  useEffect(() => {
    dispatch(changeDisplayedDiseasesAction(diseases));

    const diagnosed = diseases.filter((d) => d.is_diagnose) || [];
    setDiagnosis(diagnosed);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diseases]);

  useEffect(() => {
    if (!diseasesToRemove?.length && !!diseasesSelectedToRemove?.length) {
      dispatch(changeDiseasesSelectedToRemoveAction([]));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diseasesToRemove]);

  const columns = [
    {
      field: "actions",
      sortable: false,
      headerName: " ",
      width: 10,
      renderCell: (params) => {
        const isDiseaseInPatient = !!diseases.find((s) => s.id === params.row.id);
        const isDiseaseToDelete = !!diseasesSelectedToRemove.find((s) => s?.toString() === params.row?.id?.toString());

        return (
          <div className={classes.actionsCell}>
            {isDiseaseInPatient && !isDiseaseToDelete ? (
              <>
                <Checkbox
                  disabled={!isDiseaseInPatient}
                  size="small"
                  checked={isDiseaseSelected(params.row.id)}
                  onChange={() => handleSelectDisease(params.row.id)}
                  inputProps={{ "aria-label": "primary checkbox" }}
                />
              </>
            ) : null}
          </div>
        );
      },
    },
    {
      field: "title",
      [mobileVersion ? "width" : "flex"]: mobileVersion ? 500 : 0.6,
      headerName: intl.formatMessage({ id: "disease" }),
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <Typography>{params.row.title}</Typography>
          </>
        );
      },
    },
    {
      field: "",
      width: 80,
      sortable: false,
      align: "center",
      renderCell: (params) => {
        return (
          <div className={classes.iconContainer}>
            {params.row?.is_family_background && (
              <img className={classes.svgIcon} src={familyBackgroundIcon} alt={"Family Background Icon"} />
            )}
            {params.row?.is_patient_background && (
              <img className={classes.svgIcon} src={patientBackgroundIcon} alt={"Patient Background Icon"} />
            )}
          </div>
        );
      },
    },
    {
      field: "is_diagnose",
      width: 115,
      headerName: intl.formatMessage({ id: "isDiagnose" }),
      sortable: false,
      renderCell: (params) => {
        const isDiseaseInPatient = !!diseases.find((s) => s.id === params.row.id);

        return (
          <CustomToggleComponent
            checked={!!params.row.is_diagnose}
            onChange={() => handleUpdateDiagnosis(params.row)}
            disabled={!isDiseaseInPatient}
          />
        );
      },
    },
    {
      field: "status",
      headerName: " ",
      width: 115,
      renderCell: (params) => {
        const isDiseaseInPatient = !!diseases.find((s) => s.id === params.row.id);
        const isDiseaseToDelete = !!diseasesSelectedToRemove.find((s) => s?.toString() === params.row?.id?.toString());

        return (
          <div className={classes.actionsCell}>
            {isDiseaseToDelete ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={intl.formatMessage({ id: "status_to_be_deleted" })}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleRestoreDisease(params.row.id, false)}
                  >
                    <RemoveRoundedIcon color={"secondary"} />
                  </IconButton>
                </Tooltip>
              </div>
            ) : isDiseaseInPatient ? null : (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={intl.formatMessage({ id: "status_to_be_added" })}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleRestoreDisease(params.row.id, true)}
                  >
                    <AddRoundedIcon color={"secondary"} />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const handleDiseasesModal = () => {
    setOpenModal((openModal) => !openModal);
  };

  const handleDeleteSelectedDiseases = () => {
    restoreDiseases();
    dispatch(changeDiseasesSelectedToRemoveAction(diseasesToRemove));
  };

  const handleAddDisease = () => {
    restoreDiseases();
    handleDiseasesModal();
  };

  const restoreDiseases = () => {
    const newArray = diseases.concat(selectedDiseases);

    dispatch(changeDisplayedDiseasesAction(newArray));
  };

  const patchIsDiagnoseCallback = (d) => {
    const newDiseases = [...diseases];

    const index = diseases.indexOf(diseases.find((disease) => disease.id === d.id));
    newDiseases[index] = d;

    dispatch(storePatientDiseasesActionCreator(newDiseases));
    dispatch(changeDisplayedDiseasesAction(newDiseases));

    const diagnosis = newDiseases.filter((d) => d.is_diagnose) || [];
    dispatch(storePatientDiagnosisActionCreator(diagnosis));
  };

  const getDiseasesCallback = (response) => {
    dispatch(storePatientDiseasesActionCreator(response.data));

    const diagnosis = response.data.find((d) => d.is_diagnose) || null;
    dispatch(storePatientDiagnosisActionCreator(diagnosis));
  };

  const getDiseases = () => {
    getPatientDiseasesService(patientData.id, getDiseasesCallback);
  };

  const handleUpdateDiagnosis = (d) => {
    const body = {
      is_diagnose: !d?.is_diagnose,
    };

    const newDisease = { ...d, is_diagnose: !d?.is_diagnose };

    patchIsDiagnoseService(patientData.id, d.id, body, () => patchIsDiagnoseCallback(newDisease), getDiseases);
  };

  const handleRestoreDisease = (id, add) => {
    if (add) {
      const array = displayedDiseases.filter((d) => d.id !== id);
      dispatch(changeSelectedDiseasesAction(array));
      dispatch(changeDisplayedDiseasesAction(array));
    } else {
      const copy = [...diseasesSelectedToRemove];
      const filtered = copy.filter((d) => d !== id);
      dispatch(changeDiseasesSelectedToRemoveAction(filtered));
      dispatch(changeDiseasesToRemoveAction(filtered));
    }
  };

  return (
    <div className={classes.dataContainer}>
      <div className={classes.dataHeader}>
        <div className={classes.header}>
          <Typography variant={"h6"}>
            {intl.formatMessage({ id: "patient" })} {newPatient ? "" : patientData?.patient_id}
          </Typography>
          <Typography variant={"h6"} className={classes.headerSubtitle}>
            / {intl.formatMessage({ id: "results" })}
          </Typography>
        </div>
        <div className={classes.tableButtons}>
          {mobileVersion ? (
            <div className={classes.iconButton}>
              <Add onClick={handleDiseasesModal} />
            </div>
          ) : (
            <Button className={classes.secondaryButton} onClick={handleDiseasesModal}>
              <Typography className={classes.firstCap}>{intl.formatMessage({ id: "addDisease" })}</Typography>
            </Button>
          )}
          {mobileVersion ? (
            <div className={diseasesToRemove?.length ? classes.iconButton : classes.iconButtonDisable}>
              <Delete onClick={diseasesToRemove?.length && handleDeleteSelectedDiseases} />
              {!!diseasesToRemove?.length && (
                <div className={classes.bubble}>
                  <span>{diseasesToRemove?.length}</span>
                </div>
              )}
            </div>
          ) : (
            <Button
              className={classes.primaryButton}
              onClick={handleDeleteSelectedDiseases}
              disabled={!diseasesToRemove?.length}
            >
              <Typography className={classes.firstCap}>{intl.formatMessage({ id: "delete_selection" })}</Typography>
            </Button>
          )}
        </div>
      </div>
      {!!diseases?.length && (
        <div className={classes.diagnosisContainer}>
          <div className={classes.formBlock}>
            <Typography variant={"caption"}>{intl.formatMessage({ id: "diagnosis" })}</Typography>
            <TagListComponent tagList={diagnosis} displayField={"title"} displayedList={displayedDiseases} />
          </div>
        </div>
      )}
      <div className={classes.dataGridContainer}>
        <DataGrid
          autoHeight={false}
          rows={displayedDiseases}
          columns={columns}
          disableSelectionOnClick
          pageSize={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
          loading={loading}
          sortModel={[
            {
              field: "is_diagnose",
              sort: "desc",
            },
          ]}
        />
      </div>
      {openModal && (
        <AddDiseasesDialogComponent
          openModal={openModal}
          handleDiseasesModal={handleDiseasesModal}
          handleAddDisease={handleAddDisease}
        />
      )}
    </div>
  );
};

export default PatientDiagnosisComponent;
